import React, { useState } from 'react';
import { useQueryClient, useMutation, useQuery } from '@tanstack/react-query';
import { Button } from '@mantine/core';
import { ListManagementDto } from '../../types/settings';
import { List, Plus, Edit, Share2, Trash2, Check, X, Globe } from 'lucide-react';
import { getListColorByValue } from '../../utils/colorUtils';
import { todoApi } from '../../api/todoApi';
import { useUser } from '../../contexts/UserContext';
import { CreateListForm } from '../CreateListForm';
import { EditListForm } from '../EditListForm';
import { Modal } from '../Modal';

interface ListManagementProps {
  lists: ListManagementDto[];
  onDataUpdate: () => void;
}

export const ListManagement: React.FC<ListManagementProps> = ({
  lists,
  onDataUpdate
}) => {
  const { currentUser } = useUser();
  const queryClient = useQueryClient();
  const [showCreateListForm, setShowCreateListForm] = useState(false);
  const [showEditListForm, setShowEditListForm] = useState(false);
  const [editingListId, setEditingListId] = useState<number | null>(null);
  const [createListData, setCreateListData] = useState<{ name: string; description?: string; color?: string } | null>(null);
  const [editListData, setEditListData] = useState<{ name: string; description?: string; color?: string; isShared?: boolean; sharedWith?: string[]; isPublic?: boolean } | null>(null);
  
  // Fetch complete TodoList data for all lists to show public sharing status
  const { data: fullListsData } = useQuery({
    queryKey: ['full-todolists', currentUser?.email, lists.map(l => l.id)],
    queryFn: async () => {
      if (!currentUser?.email || lists.length === 0) return [];
      
      // Fetch complete data for all lists in parallel
      const listPromises = lists.map(list => 
        todoApi.getListWithUserEmail(list.id, currentUser.email)
      );
      
      const results = await Promise.allSettled(listPromises);
      
      // Filter out failed requests and return successful ones
      return results
        .filter((result): result is PromiseFulfilledResult<any> => result.status === 'fulfilled')
        .map(result => result.value);
    },
    enabled: !!currentUser?.email && lists.length > 0,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Fetch complete TodoList data for editing
  const { data: editingTodoList } = useQuery({
    queryKey: ['todolist', editingListId, currentUser?.email],
    queryFn: () => {
      if (!editingListId || !currentUser?.email) return null;
      return todoApi.getListWithUserEmail(editingListId, currentUser.email);
    },
    enabled: !!editingListId && !!currentUser?.email,
  });

  // Helper function to get full list data for a specific list
  const getFullListData = (listId: number) => {
    return fullListsData?.find(fullList => fullList.id === listId);
  };

  // Create list mutation
  const createListMutation = useMutation({
    mutationFn: (data: { name: string; description?: string; color?: string }) => 
      todoApi.createList(data, currentUser?.email || ''),
    onSuccess: () => {
      // Invalidate all list-related queries to ensure fresh data
      queryClient.invalidateQueries({ queryKey: ['todolists'] });
      queryClient.invalidateQueries({ queryKey: ['full-todolists'] });
      setShowCreateListForm(false);
      setCreateListData(null);
      onDataUpdate();
    },
    onError: (error) => {
      console.error('Failed to create list:', error);
    },
  });

  // Update list mutation
  const updateListMutation = useMutation({
    mutationFn: async (data: { 
      name: string; 
      description?: string; 
      color?: string;
      isShared?: boolean;
      sharedWith?: string[];
      isPublic?: boolean;
    }) => {
      if (!editingTodoList) return;
      
      // First update basic list info including public sharing
      await todoApi.updateList(editingTodoList.id, {
        name: data.name,
        description: data.description,
        color: data.color,
        isPublic: data.isPublic
      }, currentUser?.email || '');
      
      // Then update sharing if it changed
      const sharingChanged = data.isShared !== editingTodoList.isShared || 
        JSON.stringify(data.sharedWith) !== JSON.stringify(editingTodoList.sharedWith);
        
      if (sharingChanged) {
        await todoApi.updateListSharing(
          editingTodoList.id, 
          data.isShared || false, 
          data.sharedWith || [], 
          currentUser?.email || ''
        );
      }
    },
    onSuccess: () => {
      // Invalidate all related queries to ensure fresh data
      queryClient.invalidateQueries({ queryKey: ['todolists'] });
      queryClient.invalidateQueries({ queryKey: ['todolist'] });
      queryClient.invalidateQueries({ queryKey: ['full-todolists'] });
      
      setShowEditListForm(false);
      setEditListData(null);
      setEditingListId(null);
      onDataUpdate();
    },
    onError: (error) => {
      console.error('Failed to update list:', error);
    },
  });

  // Delete list mutation
  const deleteListMutation = useMutation({
    mutationFn: () => {
      if (!editingTodoList) return Promise.resolve();
      return todoApi.deleteList(editingTodoList.id, currentUser?.email || '');
    },
    onSuccess: () => {
      // Clear all related cache entries when a list is deleted
      queryClient.invalidateQueries({ queryKey: ['todolists'] });
      queryClient.invalidateQueries({ queryKey: ['todos'] });
      queryClient.invalidateQueries({ queryKey: ['todolist'] });
      queryClient.invalidateQueries({ queryKey: ['full-todolists'] });
      
      setShowEditListForm(false);
      setEditListData(null);
      setEditingListId(null);
      onDataUpdate();
    },
    onError: (error) => {
      console.error('Failed to delete list:', error);
    },
  });

  const handleEditList = (list: ListManagementDto) => {
    setEditingListId(list.id);
    setShowEditListForm(true);
  };

  if (lists.length === 0) {
    return (
      <div className="settings-section">
        <div className="settings-section-header">
          <h2 className="settings-section-title">List Management</h2>
          <p className="settings-section-description">
            Configure and manage your todo lists
          </p>
        </div>
        
        <div style={{ 
          textAlign: 'center', 
          padding: '48px 24px',
          color: 'var(--app-text-secondary)'
        }}>
          <List size={48} style={{ margin: '0 auto 16px', opacity: 0.5 }} />
          <h3 style={{ margin: '0 0 8px 0', color: 'var(--app-text-primary)' }}>No Lists to Manage</h3>
          <p style={{ margin: 0, fontSize: '14px' }}>
            You don't have any lists yet. Create your first todo list to get started!
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="settings-section">
      <div className="settings-section-header">
        <h2 className="settings-section-title">List Management</h2>
        <p className="settings-section-description">
          Configure and manage your todo lists
        </p>
      </div>


      {/* Create List Button */}
      <div className="settings-group">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
          <h3 className="settings-group-title">Your Lists</h3>
          <Button
            leftSection={<Plus size={16} />}
            onClick={() => setShowCreateListForm(true)}
            variant="filled"
            size="sm"
          >
            Create List
          </Button>
        </div>
        
        <div style={{ display: 'grid', gap: '8px' }}>
          {lists.map((list) => {
            const listColor = getListColorByValue(list.color);
            return (
              <div
                key={list.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '12px',
                  border: '1px solid var(--app-border)',
                  borderRadius: '8px',
                  background: 'var(--app-surface)'
                }}
              >
                <div
                  style={{
                    width: '12px',
                    height: '12px',
                    borderRadius: '50%',
                    background: listColor?.value || '#6b7280',
                    flexShrink: 0
                  }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ 
                    fontWeight: '600', 
                    fontSize: '15px', 
                    color: 'var(--app-text-primary)',
                    marginBottom: '4px',
                    lineHeight: '1.3'
                  }}>
                    {list.name}
                  </div>
                  
                  {list.description && (
                    <div style={{ 
                      fontSize: '13px', 
                      color: 'var(--app-text-secondary)',
                      marginBottom: '8px',
                      lineHeight: '1.4',
                      fontStyle: 'italic',
                      whiteSpace: 'pre-wrap',
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      wordBreak: 'break-word'
                    }}>
                      {list.description}
                    </div>
                  )}
                  
                  <div style={{ 
                    fontSize: '12px', 
                    color: 'var(--app-text-secondary)', 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '12px',
                    flexWrap: 'wrap'
                  }}>
                    <span>{list.itemCount} tasks • {list.completedCount} completed</span>
                    
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {list.isShared && list.sharedWith.length > 0 && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <Share2 size={12} />
                          <span>Shared with {list.sharedWith.length} user{list.sharedWith.length !== 1 ? 's' : ''}</span>
                        </div>
                      )}
                      
                      {/* Super Share indicator - only show when public */}
                      {(() => {
                        const fullListData = getFullListData(list.id);
                        const isPublic = fullListData?.isPublic || false;
                        
                        // Only render anything if the list is actually public
                        if (!isPublic) return null;
                        
                        return (
                          <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '4px',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            background: 'rgba(34, 197, 94, 0.1)',
                            border: '1px solid rgba(34, 197, 94, 0.2)'
                          }}>
                            <Globe size={11} style={{ color: '#22c55e' }} />
                            <span style={{ 
                              fontSize: '10px', 
                              color: '#22c55e',
                              fontWeight: '500'
                            }}>
                              Public
                            </span>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                </div>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleEditList(list);
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '6px 12px',
                    background: 'transparent',
                    border: '1px solid var(--app-border)',
                    borderRadius: '4px',
                    color: 'var(--app-text-primary)',
                    cursor: 'pointer',
                    fontSize: '12px'
                  }}
                >
                  <Edit size={14} />
                  Edit
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Create List Modal */}
      <Modal
        isOpen={showCreateListForm}
        onClose={() => setShowCreateListForm(false)}
        title="Create New List"
        hideCloseButton={true}
        actionButtons={
          <>
            <button
              type="button"
              onClick={() => {
                if (createListData && createListData.name.trim() && createListData.color) {
                  createListMutation.mutate({
                    name: createListData.name,
                    description: createListData.description,
                    color: createListData.color,
                  });
                }
              }}
              disabled={!createListData?.name.trim() || !createListData?.color || createListMutation.isPending}
              className="header-btn header-btn-save"
              title="Create list"
              aria-label="Create list"
            >
              <Check size={16} />
            </button>
            <button
              type="button"
              onClick={() => setShowCreateListForm(false)}
              className="header-btn header-btn-close"
              title="Cancel"
              aria-label="Cancel"
            >
              <X size={16} />
            </button>
          </>
        }
      >
        <CreateListForm
          onSave={setCreateListData}
        />
      </Modal>

      {/* Edit List Modal */}
      <Modal
        isOpen={showEditListForm}
        onClose={() => {
          setShowEditListForm(false);
          setEditingListId(null);
        }}
        title="Edit List"
        hideCloseButton={true}
        actionButtons={
          <>
            <button
              type="button"
              onClick={() => {
                if (editListData && editListData.name.trim()) {
                  updateListMutation.mutate({
                    name: editListData.name,
                    description: editListData.description,
                    color: editListData.color,
                    isShared: editListData.isShared,
                    sharedWith: editListData.sharedWith,
                    isPublic: editListData.isPublic,
                  });
                }
              }}
              disabled={!editListData?.name.trim() || updateListMutation.isPending || !editingTodoList}
              className="header-btn header-btn-save"
              title="Save changes"
              aria-label="Save changes"
            >
              <Check size={16} />
            </button>
            <button
              type="button"
              onClick={() => {
                deleteListMutation.mutate();
              }}
              disabled={deleteListMutation.isPending || !editingTodoList || editingTodoList.isDefault}
              className="header-btn header-btn-delete"
              title={editingTodoList?.isDefault ? "Cannot delete default list" : "Delete list"}
              aria-label="Delete list"
            >
              <Trash2 size={16} />
            </button>
            <button
              type="button"
              onClick={() => {
                setShowEditListForm(false);
                setEditingListId(null);
              }}
              className="header-btn header-btn-close"
              title="Cancel"
              aria-label="Cancel"
            >
              <X size={16} />
            </button>
          </>
        }
      >
        {editingTodoList ? (
          <EditListForm
            list={editingTodoList}
            onSave={setEditListData}
            onClose={() => {
              setShowEditListForm(false);
              setEditingListId(null);
            }}
          />
        ) : (
          <div style={{ padding: '20px', textAlign: 'center' }}>
            <div>Loading list details...</div>
          </div>
        )}
      </Modal>
    </div>
  );
};
